import React, {
  createContext, useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  DndContext, DragOverlay,
  MouseSensor,
  pointerWithin,
  useSensor,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { DraggedItem, useFileDragAndDrop } from '../hooks/useFileDragAndDrop';
import { TreeFile, TreeFolder } from '../services';
import { useActiveFolderData } from '../../../hooks/useActiveFolderData';
import { useMultiSelection } from '../hooks/useMultiSelection';
import { pluralize } from '../../../utils/language';
import { useFileExplorerContext } from '../../../hooks/useFileExplorerContext';

interface fileExplorerDragContextValue {
  activeItem: DraggedItem | null;
  droppedItems: DraggedItem[];
  targetFolder: TreeFolder | null;
  clearDropState: () => void;
  clearSelection: () => void;
  selectedKeys: Set<string | number>;
  getSelectedItems: () => (TreeFolder | TreeFile)[];
  handleSelection: (node: TreeFolder | TreeFile, index: number, e: React.MouseEvent) => void;
}

const FileExplorerDragContext = createContext<fileExplorerDragContextValue | null>(null);

export function useFileExplorerDragContext() {
  const context = useContext(FileExplorerDragContext);
  if (!context) {
    throw new Error('useFileExplorerDragContext must be used within FileExplorerDragProvider');
  }
  return context;
}

interface FileExplorerDndProviderProps {
  children: React.ReactNode;
}

export function FileExplorerDragProvider({ children }: FileExplorerDndProviderProps) {
  const { data } = useActiveFolderData();
  const { activeFolder } = useFileExplorerContext();
  const {
    selectedKeys,
    getSelectedItems,
    handleSelection,
    clearSelection,
  } = useMultiSelection<TreeFolder | TreeFile>(
    [...data?.data.folders ?? [], ...data?.data.files ?? []],
    (item) => {
      const prefix = 'category' in item ? 'file' : 'folder';
      return `${prefix}-${item.id}`;
    }
  );
  const {
    activeItem,
    droppedItems,
    targetFolder,
    onDragStart,
    onDragEnd,
    onDragCancel,
    clearDropState,
  } = useFileDragAndDrop();
  const sensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 5,
    },
  });
  const contextValue = useMemo(() => ({
    activeItem,
    droppedItems,
    targetFolder,
    clearDropState,
    clearSelection,
    handleSelection,
    getSelectedItems,
    selectedKeys,
  }), [activeItem,
    droppedItems,
    targetFolder, clearDropState, clearSelection, handleSelection, getSelectedItems, selectedKeys]);

  const dragOverlayCount = activeItem && selectedKeys.has(`${activeItem.type}-${activeItem.id}`)
    ? selectedKeys.size
    : 1;

  useEffect(() => {
    clearDropState();
    clearSelection();
  }, [activeFolder, clearDropState, clearSelection]);
  return (
    <FileExplorerDragContext.Provider value={contextValue}>
      <DndContext
        sensors={[sensor]}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
        collisionDetection={pointerWithin}
      >
        {children}
        <DragOverlay
          dropAnimation={null}
          modifiers={[snapCenterToCursor]}
        >
          {activeItem && (
            <div
              style={{ userSelect: 'none', width: 'fit-content' }}
              className="p-2 bg-white rounded shadow-lg fs-xs"
            >
              Estás moviendo
              {' '}
              {dragOverlayCount}
              {' '}
              {pluralize(dragOverlayCount, 'elemento', 'elementos')}
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </FileExplorerDragContext.Provider>
  );
}
